import React, { useState } from 'react';
import { FileText, Download, MessageCircle, Mail, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

// Per-project branded PDF: in-app image preview (renders in every browser) +
// download / open / WhatsApp / email share of the full PDF.
const PdfShare = ({ project, pdfUrl, waShareUrl, mailShareUrl, t }) => {
  const [open, setOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const previewUrl = `${pdfUrl}/preview.png`;
  const fileName = `${(project.name || 'project').replace(/\s+/g, '-')}-GOBRA.pdf`;

  const openModal = () => { setImgLoaded(false); setImgError(false); setOpen(true); };

  return (
    <>
      <Button onClick={openModal} data-testid="project-pdf-btn"
        className="w-full bg-gobra-green hover:bg-gobra-green-dark text-white rounded-full mb-2">
        <FileText className="h-4 w-4 mr-2" /> {t('view_pdf') || 'View & Share PDF'}
      </Button>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="border-[#25D366] text-[#1ebe57] hover:bg-[#25D366] hover:text-white rounded-full">
          <a href={waShareUrl} target="_blank" rel="noopener noreferrer" data-testid="share-pdf-whatsapp">
            <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
          </a>
        </Button>
        <Button asChild variant="outline" className="border-gobra-slate text-gobra-slate hover:bg-gobra-slate hover:text-white rounded-full">
          <a href={mailShareUrl} data-testid="share-pdf-email">
            <Mail className="h-4 w-4 mr-2" /> {t('email')}
          </a>
        </Button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-3 md:p-6"
          onClick={() => setOpen(false)} data-testid="pdf-modal-overlay">
          <div className="bg-white rounded-xl w-full max-w-3xl flex flex-col overflow-hidden shadow-2xl"
            style={{ height: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gobra-sand shrink-0">
              <h3 className="font-title text-gobra-slate text-lg font-semibold truncate">{project.name} — PDF</h3>
              <div className="flex items-center gap-3">
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-gobra-green hover:underline flex items-center gap-1" data-testid="pdf-open-newtab">
                  <ExternalLink className="h-4 w-4" /> {t('open_new_tab') || 'Open in new tab'}
                </a>
                <button onClick={() => setOpen(false)} aria-label="Close" data-testid="pdf-modal-close"
                  className="h-8 w-8 rounded-full hover:bg-gobra-sand flex items-center justify-center text-gobra-slate">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 bg-gobra-sand overflow-auto flex justify-center">
              {!imgLoaded && !imgError && (
                <div className="h-full w-full flex items-center justify-center text-gobra-slate" data-testid="pdf-loading">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" /> {t('generating_pdf') || 'Generating PDF…'}
                </div>
              )}
              {imgError && (
                <div className="h-full w-full flex items-center justify-center text-destructive text-sm px-6 text-center">
                  {t('pdf_error') || 'Could not generate the PDF. Please try again.'}
                </div>
              )}
              <img src={previewUrl} alt={`${project.name} PDF preview`} data-testid="pdf-preview-image"
                onLoad={() => setImgLoaded(true)} onError={() => setImgError(true)}
                className={`w-full h-auto object-contain ${imgLoaded ? 'block' : 'hidden'}`} />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 px-5 py-4 border-t border-gobra-sand shrink-0">
              <Button asChild className="flex-1 bg-gobra-green hover:bg-gobra-green-dark text-white rounded-full">
                <a href={pdfUrl} download={fileName} data-testid="pdf-download-btn">
                  <Download className="h-4 w-4 mr-2" /> {t('download_pdf') || 'Download PDF'}
                </a>
              </Button>
              <Button asChild variant="outline" className="flex-1 border-[#25D366] text-[#1ebe57] hover:bg-[#25D366] hover:text-white rounded-full">
                <a href={waShareUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="flex-1 border-gobra-slate text-gobra-slate hover:bg-gobra-slate hover:text-white rounded-full">
                <a href={mailShareUrl}>
                  <Mail className="h-4 w-4 mr-2" /> {t('email')}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PdfShare;
